import { useForm } from 'react-hook-form';
import Button from '@monofront/design-system/components/Button';
import { Dispatch, SetStateAction, useEffect, type JSX } from 'react';
import { useResetPassword } from '@hooks/businessContainer';
import { useTranslations } from 'use-intl';
import { EMAIL_REGEX } from '@monofront/business/constants';
import useFirebaseErrors from '@hooks/vendor/useFirebaseErrors';
import { trackEvent } from '@monofront/tracking';
import {
  Input,
  TextInput,
  ErrorText,
} from '@monofront/design-system/components/FormV2';

interface IFormResetPassword {
  toggleResetPasswordForm: Dispatch<SetStateAction<boolean>>;
  formCss: JSX.Element;
  message: string;
}

const FormResetPassword: React.FC<IFormResetPassword> = ({
  toggleResetPasswordForm,
  message,
}) => {
  const { MAPPING_RESETPASSWORD_ERROR } = useFirebaseErrors();
  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    formState: { touchedFields, dirtyFields },
    trigger,
  } = useForm();

  const isValidEmail = 'email' in dirtyFields && !errors.email;

  const {
    passwordResetEmail,
    errorPasswordResetEmail,
    setPasswordResetEmail,
    handleResetPassword,
    success,
  } = useResetPassword();

  const tAccount = useTranslations('Account');

  const onResetPassword = (user) => {
    trackEvent({ type: 'LFRequestResetPassword' });
    setPasswordResetEmail(user.email);
  };

  useEffect(() => {
    if (errorPasswordResetEmail) {
      setError('formResetPassword', { type: errorPasswordResetEmail });
    }
  }, [errorPasswordResetEmail]);

  useEffect(() => {
    if (passwordResetEmail) {
      handleResetPassword();
    }
  }, [passwordResetEmail]);

  return (
    <>
      {errors.formResetPassword && (
        <div className="error">
          <p className="error tw-lf-error-message">
            {
              MAPPING_RESETPASSWORD_ERROR[
                errors.formResetPassword.type as string
              ]
            }
          </p>
        </div>
      )}
      {success ? (
        <section className="form">
          <h4>{tAccount('updatePassword')}</h4>
          <p>
            {tAccount('resetPasswordEmailSent', { email: passwordResetEmail })}
          </p>
          <Button
            hasMinWidth={false}
            onClick={() => toggleResetPasswordForm(false)}
          >
            {tAccount('continue')}
          </Button>
        </section>
      ) : (
        <form
          onSubmit={handleSubmit(onResetPassword)}
          className="tw-flex tw-flex-col tw-gap-4"
        >
          {message && <p className="message">{message}</p>}
          <h4>{tAccount('updatePassword')}</h4>
          <p>{tAccount('resetPasswordEmailWillBeSent')}</p>
          <TextInput
            id="register-email"
            isValid={isValidEmail}
            isInvalid={touchedFields?.email && errors.email}
          >
            <Input
              type="email"
              placeholder={tAccount('email')}
              {...register('email', {
                required: true,
                pattern: EMAIL_REGEX,
                onChange: () => trigger(),
              })}
            />
            {touchedFields?.email && errors.email && (
              <ErrorText>{tAccount('pleaseUseAValidEmail')}</ErrorText>
            )}
          </TextInput>
          <a
            className="tw-lf-link-md"
            onClick={() => toggleResetPasswordForm(false)}
          >
            {tAccount('backToLogin')}
          </a>
          <Button hasMinWidth={false} type="submit" disabled={!isValidEmail}>
            {tAccount('submit')}
          </Button>
        </form>
      )}
    </>
  );
};

export default FormResetPassword;
